import React, { createContext, useState, useEffect, useCallback, useRef } from "react";
import { jwtDecode } from "jwt-decode";

// Обновлённые импорты — совместимые с новой моделью (HTTP-only + память)
import {
    authApi,
    usersApi,
    getAccessToken,
    setAccessToken,
    clearAccessToken,
    clearRefreshToken
} from "../api";

export const UserContext = createContext(undefined);

export const UserProvider = ({ children }) => {
    const [accessToken, setAccessTokenState] = useState(() => {
        // При инициализации восстанавливаем токен из localStorage
        const token = getAccessToken();
        console.log('[UserContext] Initial token:', token ? 'exists' : 'none');
        return token;
    });
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // Восстановление пользователя при загрузке приложения (один раз)
    const hasRestoredRef = useRef(false);
    useEffect(() => {
        if (hasRestoredRef.current) return;
        hasRestoredRef.current = true;

        const restoreUser = async () => {
            const token = getAccessToken();
            if (!token) {
                setLoading(false);
                return;
            }

            try {
                const user = await usersApi.current();
                console.log('[UserContext] User loaded:', user);
                // usersApi.current() возвращает profiles — используем их
                if (user.profiles && user.profiles.length > 0) {
                    user.profileId = user.profiles[0].id;
                }
                setCurrentUser(user);
            } catch (error) {
                // Fallback: декодируем JWT токен
                try {
                    const decoded = jwtDecode(token);
                    setCurrentUser(decoded);
                } catch (jwtError) {
                    if (error?.response?.status === 401) {
                        clearAccessToken();
                    }
                }
            } finally {
                setLoading(false);
            }
        };

        restoreUser();
    }, []);

    // Синхронизация состояния с глобальным accessToken
    useEffect(() => {
        const handleAuthChange = () => {
            const token = getAccessToken();
            setAccessTokenState(token);
            if (!token) setCurrentUser(null);
        };

        window.addEventListener('authChange', handleAuthChange);
        return () => window.removeEventListener('authChange', handleAuthChange);
    }, []);

    // usersApi.current() вызывается только в restoreUser (при старте) и signIn
    // чтобы избежать дублирования запросов и 429

    // Обновление accessToken через API (не напрямую)
    const updateAccessToken = useCallback((token) => {
        if (token) {
            setAccessToken(token); // Сохраняет в память и генерирует событие
        } else {
            clearAccessToken(); // Очищает и генерирует событие
        }
    }, []);

    const signIn = useCallback(async (credentials) => {
        const resp = await authApi.login(credentials);
        const { accessToken: newToken } = resp;

        try {
            // Загружаем полные данные пользователя с профилями
            const user = await usersApi.current();
            if (user.profiles && user.profiles.length > 0) {
                user.profileId = user.profiles[0].id;
            }
            setCurrentUser(user);
        } catch (err) {
            // Fallback: декодируем JWT
            try {
                const decoded = jwtDecode(newToken);
                setCurrentUser(decoded);
            } catch {
                setCurrentUser(null);
            }
        }
    }, []);

    const signOut = useCallback(async () => {
        try {
            await authApi.logout(); // Сервер: инвалидация refresh-токена
        } catch (error) {
            // Игнорируем ошибки сети/аутентификации
        } finally {
            clearAccessToken();
            clearRefreshToken();
            setCurrentUser(null);
        }
    }, []);

    // Автоматическое обновление сессии каждые 30 секунд
    useEffect(() => {
        let mounted = true;
        const intervalId = setInterval(async () => {
            if (!mounted || !accessToken) return;

            try {
                const decoded = jwtDecode(accessToken);
                const exp = decoded.exp * 1000;
                const timeUntilExpiry = exp - Date.now();

                // Если токен истекает менее чем через минуту — обновляем
                if (timeUntilExpiry < 60_000) {
                    await authApi.refresh(); // Получает новый accessToken
                    if (!mounted) return;

                    const newToken = getAccessToken();
                    if (newToken && newToken !== accessToken) {
                        setAccessTokenState(newToken); // Обновляем локальное состояние
                    }
                }
            } catch (err) {
                console.error('[UserContext] Token refresh failed:', err);
                if (mounted) signOut();
            }
        }, 30_000);

        return () => {
            mounted = false;
            clearInterval(intervalId);
        };
    }, [accessToken, signOut]);

    return (
        <UserContext.Provider
            value={{
                currentUser,
                setCurrentUser,
                setAccessToken: updateAccessToken,
                signIn,
                signOut,
                loading,
            }}
        >
            {children}
        </UserContext.Provider>
    );
};